import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { SEOHead } from '../components/SEOHead';
import { getSaveaPhone, getSaveaPhoneTel, getSpecialistMessage } from '../config/phoneConfig';
import { getProviderDataBySlug, PROVIDERS_DATA } from '../config/providersData';
import { StickyPhoneBar } from '../components/StickyPhoneBar';
import { 
  Phone, 
  Zap,
  Home,
  TrendingDown,
  Clock,
  ChevronRight,
  Leaf,
  Shield,
  Wifi,
  Globe
} from 'lucide-react';

// Configuration des logos concurrents par pays/vertical
const COMPETITOR_LOGOS = {
  'FR-electricity': [
    { src: '/images/logos/FAzUgTB.png', alt: 'EDF' },
    { src: '/images/logos/FAzUZYv.png', alt: 'Engie' },
    { src: '/images/logos/FAzg1MF.png', alt: 'TotalEnergies' },
    { src: '/images/logos/FAzgJGn.png', alt: 'Enedis' },
  ],
  'FR-internet': [
    { src: '/images/logos/FA57iF9.png', alt: 'Orange' },
    { src: '/images/logos/FA57L8u.png', alt: 'Free' },
    { src: '/images/logos/FA57tuj.png', alt: 'Bouygues' },
    { src: '/images/logos/FA57Qyb.png', alt: 'SFR' },
  ],
  'ES-electricity': [
    { src: '/images/logos/FAzUPCF.png', alt: 'Endesa' },
    { src: '/images/logos/FAzgKv4.png', alt: 'Iberdrola' },
    { src: '/images/logos/FAzgzGe.png', alt: 'Naturgy' },
    { src: '/images/logos/FAzgYuV.png', alt: 'Repsol' },
  ],
  'ES-internet': [
    { src: '/images/logos/FAzgxn9.png', alt: 'Movistar' },
    { src: '/images/logos/FAzgMFa.png', alt: 'Vodafone' },
    { src: '/images/logos/FA57bZQ.png', alt: 'MásMóvil' },
    { src: '/images/logos/FAzUSpV.png', alt: 'Digi' },
  ],
};

// Logos croisés pour le groupe GEG/Bellénergie/GdB/Mint
const GROUP_LOGOS = {
  'GEG': { src: '/images/logos/geg-logo.svg', alt: 'GEG' },
  'La Bellénergie': { src: '/images/logos/K0Rz9Uu.png', alt: 'La Bellénergie' },
  'Gaz de Bordeaux': { src: '/images/logos/gaz-de-bordeaux-logo.svg', alt: 'Gaz de Bordeaux' },
  'Mint Energie': { src: '/images/logos/FAzgnZ7.png', alt: 'Mint Energie' },
};
const GROUP_NAMES = Object.keys(GROUP_LOGOS);

const getBottomLogos = (provider) => {
  if (GROUP_NAMES.includes(provider.name)) {
    return GROUP_NAMES.filter(n => n !== provider.name).map(n => GROUP_LOGOS[n]);
  }
  const logoKey = `${provider.country}-${provider.vertical}`;
  return COMPETITOR_LOGOS[logoKey] || COMPETITOR_LOGOS['FR-electricity'];
};

// Couleurs de la sticky bar par fournisseur
const STICKY_BAR_COLORS = {
  'EDF': 'bg-blue-700 hover:bg-blue-800',
  'Engie': 'bg-cyan-600 hover:bg-cyan-700',
  'TotalEnergies': 'bg-red-600 hover:bg-red-700',
  'Endesa': 'bg-blue-600 hover:bg-blue-700',
  'Digi Mobil': 'bg-cyan-500 hover:bg-cyan-600',
  'default': 'bg-emerald-600 hover:bg-emerald-700'
};

export const ProviderPageV2 = ({ providerSlug }) => {
  const { i18n } = useTranslation();
  const location = useLocation();
  const [competitors, setCompetitors] = useState([]);
  const [provider, setProvider] = useState(null);
  
  // Déterminer le slug depuis l'URL si non fourni
  const urlParts = location.pathname.split('/').filter(part => part !== '');
  const slug = providerSlug || urlParts[urlParts.length - 1];
  
  useEffect(() => {
    window.scrollTo(0, 0);
    
    const providerData = getProviderDataBySlug(slug);
    if (providerData) {
      setProvider(providerData);
      
      // Charger les concurrents du même pays/vertical (dédupliqués par nom)
      const allProviders = Object.values(PROVIDERS_DATA);
      const uniqueProviders = allProviders.filter((p, index, self) => 
        self.findIndex(t => t.name === p.name) === index
      );
      const sameTypeProviders = uniqueProviders.filter(
        p => p.country === providerData.country && 
             p.vertical === providerData.vertical && 
             p.name !== providerData.name
      );
      const shuffled = sameTypeProviders.sort(() => 0.5 - Math.random());
      setCompetitors(shuffled.slice(0, 8));
    }
  }, [slug, location.pathname]);

  if (!provider) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Chargement...</h1>
        </div>
      </div>
    );
  }

  const isSpanish = provider.country === 'ES';
  const isInternet = provider.vertical === 'internet';
  const country = provider.country;
  const vertical = provider.vertical;
  const specialist = getSpecialistMessage(country, vertical);
  
  // Mois actuel automatique
  const monthNames = isSpanish 
    ? ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre']
    : ['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'];
  const now = new Date();
  const currentMonth = `${monthNames[now.getMonth()]} ${now.getFullYear()}`;
  
  // Téléphones SAVEA (avec override possible par fournisseur)
  const saveaPhone = provider.phone_override?.display || getSaveaPhone(country, vertical, 'LP');
  const saveaPhoneTel = provider.phone_override?.tel || getSaveaPhoneTel(country, vertical, 'LP');
  const souscrirePhone = provider.phone_override?.display || getSaveaPhone(country, vertical, 'SB');
  const souscrirePhoneTel = provider.phone_override?.tel || getSaveaPhoneTel(country, vertical, 'SB');

  // Logos concurrents pour le hero
  const bottomLogos = getBottomLogos(provider).filter(logo => logo.alt !== provider.name).slice(0, 3);

  // Couleur de la sticky bar - utiliser la couleur primaire du fournisseur
  const stickyBarColor = provider.primary_color 
    ? `hover:opacity-90` 
    : 'bg-emerald-600 hover:bg-emerald-700';
  
  const stickyBarStyle = provider.primary_color 
    ? { backgroundColor: provider.primary_color }
    : {};

  // Couleur du gradient (basée sur la couleur primaire du fournisseur)
  const gradientClass = provider.primary_color === '#07387A' ? 'from-slate-900 via-slate-800 to-blue-900' :
                        provider.primary_color === '#00AAFF' ? 'from-slate-900 via-slate-800 to-cyan-900' :
                        provider.primary_color === '#FE0201' ? 'from-slate-900 via-slate-800 to-red-900' :
                        provider.primary_color === '#008ACE' ? 'from-slate-900 via-slate-800 to-blue-900' :
                        provider.primary_color === '#00AEEF' ? 'from-slate-900 via-slate-800 to-cyan-900' :
                        'from-slate-900 via-slate-800 to-emerald-900';

  // Textes selon la langue ET le vertical (énergie vs internet)
  const serviceType = isInternet 
    ? (isSpanish ? 'telecomunicaciones' : 'télécommunications')
    : (isSpanish ? 'energía' : 'énergie');
  
  const texts = {
    badge: isSpanish ? 'Servicio gratuito y sin compromiso' : 'Service gratuit et sans engagement',
    title: isSpanish ? '¿Cómo contactarnos?' : 'Comment nous contacter ?',
    subtitle: isSpanish 
      ? `Llámanos para comparar las ofertas de ${provider.name} con las de nuestros socios. Podrías reducir tu factura.`
      : `Appelez-nous pour comparer les offres de ${provider.name} avec celles de nos partenaires. Vous pourriez réduire votre facture.`,
    disclaimer: isSpanish 
      ? `SAVEA es un servicio gratuito de comparación de ofertas de ${serviceType}. Las tarifas mostradas son orientativas y pueden variar. Para obtener un presupuesto personalizado, contacte con nuestros asesores. Servicio gratuito - Llamada no recargada.`
      : `SAVEA est un service de comparaison d'offres ${isInternet ? 'internet' : "d'énergie"} gratuit. Les tarifs affichés sont fournis à titre indicatif et peuvent varier. Pour obtenir un devis personnalisé, contactez nos conseillers. Service gratuit - Appel non surtaxé.`,
    freeNumber: isSpanish ? 'Servicio Gratuito' : 'Numéro Gratuit',
    subscribe: isSpanish ? 'Contratar' : 'Souscrire',
    moving: isSpanish ? 'Mudanza' : 'Déménagement',
    hours: isSpanish ? 'Lunes - Sábado • 8h-21h' : 'Lundi - Samedi • 8h-21h',
    directNumber: isSpanish ? `Número directo ${provider.name}` : `Numéro direct ${provider.name}`,
    notSurcharged: isSpanish ? '(llamada no recargada)' : '(appel non surtaxé)',
    tariffs: isSpanish ? `Tarifas ${provider.name} - ${currentMonth}` : `Tarifs ${provider.name} - ${currentMonth}`,
    priceLabel: isInternet 
      ? (isSpanish ? 'Precios mensuales' : 'Prix mensuels') 
      : (isSpanish ? 'Precio del kWh' : 'Prix du kWh'),
    compareTitle: isSpanish ? 'Compara con otros proveedores' : 'Comparez avec d\'autres fournisseurs',
    compareSubtitle: isSpanish ? 'Nuestros asesores pueden presentarte estas alternativas' : 'Nos conseillers peuvent vous présenter ces alternatives',
    greenOffer: isSpanish ? '🌱 Oferta verde' : '🌱 Offre verte',
    classicOffer: isSpanish ? 'Oferta clásica' : 'Offre classique',
    compareBtn: isSpanish ? 'Comparar con un asesor' : 'Comparer avec un conseiller',
    aboutTitle: isSpanish ? `Acerca de ${provider.name}` : `À propos de ${provider.name}`,
    faqTitle: isSpanish ? 'Preguntas frecuentes' : 'Questions fréquentes',
    ctaTitle: isSpanish ? '¿Necesitas un consejo personalizado?' : 'Besoin d\'un conseil personnalisé ?',
    ctaSubtitle: isSpanish 
      ? `Nuestros asesores comparan las ofertas de ${provider.name} con las de nuestros socios`
      : `Nos conseillers comparent les offres de ${provider.name} avec celles de nos partenaires`,
    footerDisclaimer: isSpanish 
      ? `SAVEA es un servicio gratuito de comparación de ofertas de ${serviceType}. Las tarifas mostradas son orientativas y pueden variar. Para obtener un presupuesto personalizado, contacte con nuestros asesores. Servicio gratuito - Llamada no recargada.`
      : `SAVEA est un service de comparaison d'offres ${isInternet ? 'internet' : "d'énergie"} gratuit. Les tarifs affichés sont fournis à titre indicatif et peuvent varier. Pour obtenir un devis personnalisé, contactez nos conseillers. Service gratuit - Appel non surtaxé.`,
  };

  // FAQ dynamique
  const faqs = isInternet ? [
    {
      q: isSpanish ? `¿Cuáles son las tarifas de ${provider.name}?` : `Quels sont les tarifs de ${provider.name} ?`,
      a: isSpanish 
        ? `${provider.name} propone ofertas desde ${provider.main_price}. Nuestros asesores pueden comparar estas tarifas con las de nuestros socios.`
        : `${provider.name} propose des offres à partir de ${provider.main_price}. Nos conseillers peuvent comparer ces tarifs avec ceux de nos partenaires.`
    },
    {
      q: isSpanish ? '¿Se puede encontrar más barato?' : 'Peut-on trouver moins cher ?',
      a: isSpanish 
        ? 'Sí, otros operadores proponen ofertas más competitivas. Nuestros asesores pueden ayudarle a encontrar la mejor oferta.'
        : 'Oui, d\'autres opérateurs proposent des offres plus compétitives. Nos conseillers peuvent vous aider à trouver la meilleure offre.'
    },
    {
      q: isSpanish ? '¿Cambiar de operador es complicado?' : 'Changer d\'opérateur, est-ce compliqué ?',
      a: isSpanish 
        ? 'No, su nuevo operador se encarga de todas las gestiones de portabilidad y cambio de línea.'
        : 'Non, votre nouvel opérateur s\'occupe de toutes les démarches de portabilité et changement de ligne.'
    }
  ] : [
    {
      q: isSpanish ? `¿Cuál es la tarifa de ${provider.name}?` : `Quel est le tarif de ${provider.name} ?`,
      a: isSpanish 
        ? `${provider.name} propone tarifas desde ${provider.main_price}. Nuestros asesores pueden comparar con nuestros socios.`
        : `${provider.name} propose des tarifs à partir de ${provider.main_price}. Nos conseillers peuvent comparer avec nos partenaires.`
    },
    {
      q: isSpanish ? '¿Se puede encontrar más barato?' : 'Peut-on trouver moins cher ?',
      a: isSpanish 
        ? 'Sí, proveedores alternativos proponen ofertas hasta 15% más baratas. Contacte con nuestros asesores.'
        : 'Oui, des fournisseurs alternatifs proposent des offres jusqu\'à 15% moins chères. Contactez nos conseillers.'
    },
    {
      q: isSpanish ? '¿Cambiar de proveedor es complicado?' : 'Changer de fournisseur, est-ce compliqué ?',
      a: isSpanish 
        ? 'No, es gratuito, sin corte y sin intervención técnica. Su nuevo proveedor se encarga de todo.'
        : 'Non, c\'est gratuit, sans coupure et sans intervention technique. Votre nouveau fournisseur s\'occupe de tout.'
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      <SEOHead 
        title={`${isSpanish ? 'Comparar ofertas' : 'Comparer les offres'} ${provider.name} - ${isSpanish ? 'Número gratuito' : 'Numéro gratuit'} | Savea.info`}
        description={provider.description[isSpanish ? 'es' : 'fr']}
        keywords={`${provider.name}, ${isSpanish ? 'comparar' : 'comparer'} ${provider.name}, ${isSpanish ? 'ofertas' : 'offres'} ${provider.name}`}
        type="provider"
      />
      
      {/* HERO - Design dark premium */}
      <section className={`pt-6 pb-10 lg:pt-12 lg:pb-16 bg-gradient-to-br ${gradientClass}`}>
        <div className="max-w-lg lg:max-w-6xl mx-auto px-4">
          
          {/* Desktop: 2 colonnes / Mobile: stacked */}
          <div className="lg:grid lg:grid-cols-2 lg:gap-16 lg:items-center">
            
            {/* Colonne gauche - Branding, titre, logos */}
            <div>
              {/* SAVEA Branding */}
              <div className="flex items-center justify-center lg:justify-start gap-2 mb-4">
                <div className="w-7 h-7 lg:w-9 lg:h-9 bg-gradient-to-br from-sage-600 to-sage-700 rounded-lg flex items-center justify-center">
                  <Zap className="w-4 h-4 lg:w-5 lg:h-5 text-white" />
                </div>
                <span className="text-white font-bold text-lg lg:text-xl">SAVEA</span>
              </div>

              {/* Badge */}
              <div className="flex justify-center lg:justify-start mb-4">
                <div className="inline-flex items-center gap-2 bg-white/10 border border-white/20 rounded-full px-4 py-1.5">
                  <div className="w-2 h-2 bg-emerald-400 rounded-full animate-pulse"></div>
                  <span className="text-white/80 text-xs font-medium">{texts.badge}</span>
                </div>
              </div>
              
              {/* Titre */}
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-3 text-center lg:text-left leading-tight text-white">
                {texts.title}
              </h1>
              
              <p className="text-slate-300 text-sm lg:text-base text-center lg:text-left mb-6">
                {texts.subtitle}
              </p>
              
              {/* Logo principal + logos secondaires - fond blanc */}
              {provider.name !== 'Alpiq' && (
                <div className="bg-white rounded-2xl p-4 border border-white/30 mb-3 lg:mb-0 overflow-hidden lg:max-w-sm">
                  <div className="flex flex-col items-center gap-3">
                    <img 
                      src={provider.logo_url}
                      alt={provider.name}
                      className={`w-auto object-contain ${
                        provider.name === 'Gaz de Bordeaux' 
                          ? 'h-20 max-w-[200px]' 
                          : 'h-14 max-w-[160px]'
                      }`}
                      loading="eager"
                    />
                    {bottomLogos.length > 0 && (
                      <div className="flex items-center justify-center gap-4 w-full">
                        {bottomLogos.map((logo, index) => (
                          <img 
                            key={index}
                            src={logo.src}
                            alt={logo.alt}
                            className="h-6 w-auto max-w-[70px] object-contain opacity-60"
                            loading="eager"
                          />
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>

            {/* Colonne droite - CTA Card */}
            <div>
              {/* Disclaimer light - Seulement pour ES (avant la CTA card) */}
              {provider.country === 'ES' && (
                <p className="text-slate-400/70 text-xs text-center lg:text-left mb-3">
                  {texts.disclaimer}
                </p>
              )}

              {/* CTA Card */}
              <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-5 lg:p-8 border border-white/20">
                
                {/* Numéro principal */}
                <a 
                  href={`tel:${saveaPhoneTel}`}
                  className="w-full flex flex-col items-center justify-center text-white font-bold text-lg lg:text-xl py-4 lg:py-5 rounded-xl shadow-lg transition-all duration-300 mb-3"
                  style={{ backgroundColor: provider.primary_color }}
                  data-testid="main-cta-phone"
                >
                  <div className="flex items-center gap-3">
                    <Phone className="w-5 h-5 lg:w-6 lg:h-6" />
                    <span className="savea-phone-number">{saveaPhone}</span>
                  </div>
                  <span className="text-white/70 text-xs mt-1">{texts.freeNumber}</span>
                </a>

                {/* Boutons secondaires */}
                <div className="grid grid-cols-2 gap-3">
                  <a 
                    href={`tel:${souscrirePhoneTel}`}
                    className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 lg:py-4 px-4 rounded-xl transition-all duration-300 text-sm border border-white/10"
                    data-testid="cta-souscrire"
                  >
                    {isInternet ? <Wifi className="w-4 h-4 text-blue-400" /> : <Zap className="w-4 h-4 text-yellow-400" />}
                    <span>{texts.subscribe}</span>
                  </a>
                  <a 
                    href={`tel:${saveaPhoneTel}`}
                    className="flex items-center justify-center gap-2 bg-white/10 hover:bg-white/20 text-white font-medium py-3 lg:py-4 px-4 rounded-xl transition-all duration-300 text-sm border border-white/10"
                    data-testid="cta-demenagement"
                  >
                    <Home className="w-4 h-4 text-emerald-400" />
                    <span>{texts.moving}</span>
                  </a>
                </div>

                {/* Horaires */}
                <div className="text-center mt-4 text-slate-300 text-xs">
                  <span className="inline-flex items-center gap-1">
                    <Clock className="w-3 h-3" />
                    {texts.hours}
                  </span>
                </div>
                
                {/* Disclaimer - Seulement pour FR (après les horaires) */}
                {provider.country === 'FR' && (
                  <p className="text-slate-400/70 text-xs text-center mt-3">
                    {texts.disclaimer}
                  </p>
                )}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Numéro direct fournisseur */}
      <section className="py-4 bg-blue-50 border-y border-blue-100">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-center text-gray-700 text-sm">
            {texts.directNumber} : <span className="font-semibold text-blue-800">{provider.phone_direct}</span> <span className="text-gray-500 text-xs">{texts.notSurcharged}</span>
          </p>
        </div>
      </section>

      {/* Tarifs */}
      {provider.offers && provider.offers.length > 0 && (
        <section className="py-10 bg-gray-50">
          <div className="max-w-4xl mx-auto px-4">
            <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
              {texts.tariffs}
            </h2>
            <p className="text-gray-500 text-sm text-center mb-6">
              {texts.priceLabel}
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              {provider.offers.slice(0, 3).map((offer, index) => (
                <Card key={index} className="border-gray-200 bg-white">
                  <CardContent className="p-5">
                    <div className="flex justify-between items-start mb-3">
                      <h3 className="font-semibold text-gray-900 text-sm">{offer.name}</h3>
                      {offer.green && (
                        <span className="bg-green-100 text-green-700 text-xs px-2 py-1 rounded flex items-center gap-1">
                          <Leaf className="w-3 h-3" /> {isSpanish ? 'Verde' : 'Verte'}
                        </span>
                      )}
                    </div>
                    <div className="text-2xl font-bold mb-2" style={{ color: provider.primary_color }}>
                      {offer.price_kwh || offer.price || provider.main_price}
                    </div>
                    <p className="text-gray-500 text-xs">
                      {offer.description?.[isSpanish ? 'es' : 'fr'] || ''}
                    </p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Comparer avec d'autres fournisseurs */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-2 text-center">
            {texts.compareTitle}
          </h2>
          <p className="text-gray-500 text-sm text-center mb-6">
            {texts.compareSubtitle}
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {competitors.slice(0, 8).map((comp, index) => (
              <div 
                key={index} 
                className="flex items-center justify-between bg-gray-50 p-4 rounded-xl border border-gray-200 hover:border-emerald-300 hover:shadow-md transition-all"
              >
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center overflow-hidden">
                    <img 
                      src={comp.logo_url}
                      alt={comp.name}
                      className="max-w-full max-h-full object-contain"
                      loading="lazy"
                    />
                  </div>
                  <div>
                    <h3 className="font-semibold text-gray-900 text-sm">{comp.name}</h3>
                    <p className="text-xs text-gray-500">
                      {comp.offers?.[0]?.green ? texts.greenOffer : texts.classicOffer}
                    </p>
                  </div>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-emerald-600">{comp.main_price}</div>
                </div>
              </div>
            ))}
          </div>
          
          {/* CTA */}
          <div className="text-center mt-8">
            <a 
              href={`tel:${saveaPhoneTel}`}
              className="inline-flex items-center gap-2 text-white font-semibold py-3 px-6 rounded-full transition-all"
              style={{ backgroundColor: provider.primary_color }}
            >
              <Phone className="w-4 h-4" />
              <span>{texts.compareBtn}</span>
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* À propos */}
      <section className="py-10 bg-gray-50">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            {texts.aboutTitle}
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${provider.primary_color}20` }}>
                    {isInternet ? <Globe className="w-5 h-5" style={{ color: provider.primary_color }} /> : <Zap className="w-5 h-5" style={{ color: provider.primary_color }} />}
                  </div>
                  <h3 className="font-semibold text-gray-900">{provider.key_features?.[0]?.[isSpanish ? 'es' : 'fr'] || ''}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {provider.description[isSpanish ? 'es' : 'fr']}
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <TrendingDown className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{isSpanish ? '¿Se puede encontrar más barato?' : 'Peut-on faire moins cher ?'}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {isSpanish 
                    ? 'Sí, proveedores alternativos proponen ofertas más competitivas. Contactando con nuestros asesores, podría ahorrar en su factura.'
                    : 'Oui ! Des fournisseurs alternatifs proposent des offres plus compétitives. En contactant nos conseillers, vous pourriez économiser sur votre facture.'
                  }
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Leaf className="w-5 h-5 text-green-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{provider.key_features?.[1]?.[isSpanish ? 'es' : 'fr'] || ''}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {provider.advantages?.[0]?.[isSpanish ? 'es' : 'fr'] || ''}. {provider.advantages?.[1]?.[isSpanish ? 'es' : 'fr'] || ''}.
                </p>
              </CardContent>
            </Card>

            <Card className="border-gray-200">
              <CardContent className="p-5">
                <div className="flex items-start gap-3 mb-3">
                  <div className="w-10 h-10 bg-purple-100 rounded-lg flex items-center justify-center flex-shrink-0">
                    <Shield className="w-5 h-5 text-purple-600" />
                  </div>
                  <h3 className="font-semibold text-gray-900">{isSpanish ? 'Cambiar es simple' : 'Changer, c\'est simple'}</h3>
                </div>
                <p className="text-gray-600 text-sm leading-relaxed">
                  {isSpanish 
                    ? 'Cambiar de proveedor es gratuito, sin corte y sin intervención. Su nuevo proveedor se encarga de todas las gestiones.'
                    : 'Changer de fournisseur est gratuit, sans coupure et sans intervention. Votre nouveau fournisseur s\'occupe de toutes les démarches.'
                  }
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-10 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <h2 className="text-xl font-bold text-gray-900 mb-6 text-center">
            {texts.faqTitle}
          </h2>
          
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <details key={index} className="bg-gray-50 rounded-xl p-4 cursor-pointer group">
                <summary className="font-semibold text-gray-900 flex items-center justify-between">
                  {faq.q}
                  <ChevronRight className="w-5 h-5 text-gray-400 group-open:rotate-90 transition-transform" />
                </summary>
                <p className="text-gray-600 text-sm mt-3">{faq.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Final */}
      <section className={`py-12 bg-gradient-to-br ${gradientClass}`}>
        <div className="max-w-lg mx-auto px-4 text-center">
          <h2 className="text-xl font-bold text-white mb-3">
            {texts.ctaTitle}
          </h2>
          <p className="text-slate-300 text-sm mb-6">
            {texts.ctaSubtitle}
          </p>
          <a 
            href={`tel:${saveaPhoneTel}`}
            className="inline-flex items-center justify-center gap-2 text-white font-bold py-4 px-8 rounded-full shadow-lg transition-all hover:shadow-xl"
            style={{ backgroundColor: provider.primary_color }}
          >
            <Phone className="w-5 h-5" />
            <span className="savea-phone-number">{saveaPhone}</span>
          </a>
          <p className="text-slate-400 text-xs mt-3">{texts.freeNumber}</p>
        </div>
      </section>

      {/* Footer Compliance */}
      <section className="py-6 pb-20 bg-gray-100 border-t border-gray-200">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-gray-500 text-xs text-center leading-relaxed">
            {texts.footerDisclaimer}
          </p>
        </div>
      </section>

      {/* Sticky Phone Bar */}
      <StickyPhoneBar 
        providerColorHex={provider.secondary_color || provider.primary_color}
        phoneOverrideTel={provider.phone_override?.tel}
        phoneOverrideDisplay={provider.phone_override?.display}
      />
    </div>
  );
};

export default ProviderPageV2;
